// FloatShell — Modern パネルをドックから切り離して自由配置するフローティング枠
// (docs/_wip/floating-panels-2026-08-01.md §2/§6/§9、S2、2026-08-01)。
//
// ## 同じグリップ・同じ drag 経路 (§2)
//
// ドック内パネル (`ModernPanelDragShell`) と **同じ `usePanelDragGrip` hook** を使う
// (= pointer-capture の配管が文字通り 1 本)。「どこに着地するか」(ドックへ戻すか / 別の
// 座標へ再配置するか) の判定は親 (`ModernLayout.handlePointerDragMove`) が一元的に行い、
// このコンポーネントは受け取った `position` をそのまま描くだけ (= fully controlled)。
// ドラッグ中の見た目もこの原則に従う:
//   - `draggingId === panelId` かつ「ドック外 (float 領域)」にポインタがある間は、親が
//     `floatPositionFromPointer` で算出した座標を `position` として渡す (= ポインタに追従)。
//   - `draggingId === panelId` かつ「ドック近傍 (吸着マージン内)」にポインタがある間は、
//     親は `position` を **直前のコミット済みフロート位置に固定** して渡す (= 「ドックへ
//     吸い込まれる直前」を示す視覚、パネル自体はまだ動かない。§2「ドラッグ中はドック側の
//     席を予約したまま」と対称: フロート側も pointerup までは実座標を確定させない)。
// これにより本コンポーネントは自前のドラッグ座標 state を一切持たない (= 1 つの真実)。
//
// ## 見た目
//
// Modern ネイティブの floating box (Classic の `outsetFrame` skin は使わない、§9.6 の
// 延長)。z-order は `zIndexFromOrder` (`FLOAT_Z_BASE=40`) — CanvasStage (`z-index:1`) より
// 上・モーダル/ConfirmDialog (`z-50`/`z-[60]`) より下の帯 (Classic 既存の floating 部品
// `ColorWheelPalette`/`LayerPopup`/`PenPopup` と同じ `z-40` 帯に合わせた、§9 z-order 要件)。
//
// ## 掴み所 = シェル全体 (バグ修正 2026-08-01 実機報告)
//
// 当初は上端の細い ⠿ グリップ帯 (高さ ~16px) だけが掴み所だった。しかし各パネル本体
// (`panelRegistry` の render 結果、例: `ModernLayerPanel`) は**自分自身の見出し**
// (`<header>レイヤー</header>` 等) を持つため、フロート化するとグリップ帯の直下に
// 「太いタイトルバーに見えるが掴めない」帯が重なって見える (DOM 上もラベルが2回出る:
// 「⠿レイヤー」+「レイヤー」)。ユーザーは見た目がタイトルバーである方 (= パネル自身の
// 見出し) を掴むのが自然だが、そこには何もハンドラが無いため、ブラウザの既定動作
// (テキスト選択開始 / `<img>` 等のネイティブ drag-and-drop) が代わりに発動し、薄い
// drag image がポインタに追従する「動かないウィンドウ」に見えていた
// (実機再現: 掴む位置でグリップ帯=動く / その数十 px 下=座標変化なし、を確認)。
//
// 修正: OS のウィンドウと同じく、**ボタン等インタラクティブな要素以外はシェルの
// どこを掴んでもドラッグできる**ようにする。`usePanelDragGrip` (= ドック内パネルと
// 共有する唯一の pointer-capture 実装、2 系統にしない) をシェルの root へ配線し、
// `NO_SHELL_DRAG_SELECTOR` に一致する要素 (ボタン/入力/カスタムスライダー等) の上での
// pointerdown だけは grip へ委譲せず素通りさせる (= 内側の要素が自分で
// `setPointerCapture` するのを邪魔しない)。ネイティブ drag/選択の残存経路は
// `onDragStart` の `preventDefault` (dragstart はバブルするので `<img>` 等どの子要素が
// 起点でも root で捕まえられる) で塞ぐ。
//
// engine/protocol には触れない。

import { useCallback, useLayoutEffect, useRef } from 'react';

import {
  clampFloatPosition,
  FLOAT_EDGE_INSET_PX,
  type FloatPoint,
  type FloatSize,
  NO_SHELL_DRAG_SELECTOR,
} from './floatShellGeometry.js';
import { usePanelDragGrip } from './usePanelDragGrip.js';

// NO_SHELL_DRAG_SELECTOR (= シェル全体ドラッグの対象外セレクタ) は S3 §12 で
// Classic のサイドバー一体フロートも同じ判定を必要としたため `floatShellGeometry.ts`
// へ引き上げた (= 判定系を二重に作らない)。詳細コメントはそちら参照。

const CLAMP_EPSILON_PX = 0.5;

export interface FloatShellProps {
  /** グリップ aria ラベル / タイトル表示に使う人間可読名 (= PANEL_ARIA と同語彙)。 */
  readonly label: string;
  /** 描画位置 (px、親が fully control する。上記コメント参照)。 */
  readonly position: FloatPoint;
  /** CSS z-index (= `zIndexFromOrder` で親が算出)。 */
  readonly zIndex: number;
  /** 自分がドラッグ中か (= 見た目の強調に使う)。 */
  readonly isDragging: boolean;
  /** ドラッグ中、ドック吸着圏内にいるか (= 「ドックへ戻る直前」の視覚キュー)。 */
  readonly nearDock: boolean;
  /**
   * グリップの pointerdown → 閾値超え (= ドラッグ開始)。`grabOffset` は「掴んだ点 − シェル
   * root の rect 左上」の実測値 (§11「掴んだ位置を保持する」、フロート中パネルは `FloatShell`
   * root の rect が基準)。
   */
  readonly onPointerDragStart: (grabOffset: FloatPoint) => void;
  /** ドラッグ中の pointermove (client 座標)。 */
  readonly onPointerDragMove: (clientX: number, clientY: number) => void;
  /** ドラッグ終了 (`committed`=true が pointerup、false が cancel/Escape)。 */
  readonly onPointerDragEnd: (committed: boolean) => void;
  /** クリック/ドラッグ開始で最前面へ (§9 z-order 要件)。 */
  readonly onRequestFront: () => void;
  /** 「ドックへ戻す」ボタン (= 迷子からの復帰導線、§9 導線5)。 */
  readonly onReturnToDock: () => void;
  /**
   * 「ドックへ戻す」ボタンの表示文言 (S2 §11 改訂、consult 2026-08-01「位置ベース + 方向
   * ラベル」)。親 (`ModernLayout`) が現在の画面位置 (左半分/右半分) から都度算出し、
   * `'←ドック'` / `'ドック→'` のように**行き先を矢印で予告**する。押す前に結果が見える
   * ことが狙いなので、固定文言にしない。
   */
  readonly returnToDockLabel: string;
  /** 上記ボタンの aria-label (= スクリーンリーダーでも方向が分かる文言、親が組み立てる)。 */
  readonly returnToDockAriaLabel: string;
  /**
   * window resize 後に画面外へ取り残されていたら呼ばれる、clamp 後の新しい位置
   * (= 親が `floating[panelId]` を更新して永続化する。ドラッグ中は呼ばれない)。
   */
  readonly onReposition: (next: FloatPoint) => void;
  /**
   * 実測サイズの報告 (px、`getBoundingClientRect`)。マウント直後 + `ResizeObserver` で
   * パネル内容の伸縮のたびに呼ばれる (ドラッグ中も呼ぶ)。親 (`ModernLayout`) はこれを
   * panelId ごとに保持し、ドラッグ中の描画位置 clamp と drop 確定の着地点 clamp の**両方**に
   * 使う (= `FLOAT_PANEL_FALLBACK_HEIGHT` 定数は「まだ一度も実測していない初回」だけの
   * 近似に限定する、バグ修正 2026-08-01「背の低いフロートが画面下まで移動できない」)。
   */
  readonly onMeasure: (size: FloatSize) => void;
  /** ドック時の既定幅 (= home region の既定ドック幅を流用、パネルごとに極端な幅差を防ぐ)。 */
  readonly widthPx: number;
  readonly children: React.ReactNode;
}

/**
 * フローティングパネル 1 枚の枠 (グリップ帯 + 「ドックへ戻す」+ 本体)。
 */
export const FloatShell = ({
  label,
  position,
  zIndex,
  isDragging,
  nearDock,
  onPointerDragStart,
  onPointerDragMove,
  onPointerDragEnd,
  onRequestFront,
  onReturnToDock,
  returnToDockLabel,
  returnToDockAriaLabel,
  onReposition,
  onMeasure,
  widthPx,
  children,
}: FloatShellProps): React.ReactNode => {
  const rootRef = useRef<HTMLDivElement>(null);
  // measureGrabOffset: 掴んだ点とシェル root (= フロートパネルの実際の描画矩形) 左上の相対
  // 距離を計測する (§11「フロート中パネルは FloatShell root の rect が基準」)。root は
  // `position:fixed; left:position.x; top:position.y` で描かれるため、rect.left/top は
  // 実質 `position.x/y` と一致する (= 実測することで CSS box-sizing/border の揺れも自然に
  // 吸収される)。
  const grip = usePanelDragGrip({
    onDragStart: onPointerDragStart,
    onDragMove: onPointerDragMove,
    onDragEnd: onPointerDragEnd,
    measureGrabOffset: (e) => {
      const rect = rootRef.current?.getBoundingClientRect();
      if (!rect) return { x: 0, y: 0 };
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    },
  });

  // シェル全体を掴み所にする pointerdown 入口。`NO_SHELL_DRAG_SELECTOR` に一致する要素の
  // 上 (ボタン/入力/カスタムスライダー等) では grip へ委譲せず、その要素が自分で
  // `pointerdown` を処理するのに任せる (= `setPointerCapture` の奪い合いを避ける)。
  // pointermove/up/cancel は `usePanelDragGrip` 内部で「pointerdown 時に記録した
  // pointerId と一致するか」を見てから処理するため、ここで委譲しなかった (=
  // `startRef` が立たなかった) pointerId の move/up はそのまま no-op になる — 個別に
  // 除外ロジックを重複させる必要はない。
  const handleShellPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>): void => {
      if ((e.target as Element | null)?.closest(NO_SHELL_DRAG_SELECTOR)) return;
      grip.onPointerDown(e);
    },
    [grip.onPointerDown],
  );

  // 実測 (= onMeasure 報告) + 画面外救済 (§9「ロード時と resize 時に viewport clamp」)。
  // マウント直後 + window resize + ResizeObserver (= パネル内容の伸縮、レイヤー枚数増減等) の
  // たびに実寸 (getBoundingClientRect) を測る。
  //   - onMeasure は常に呼ぶ (ドラッグ中も): 親が panelId → 実測サイズを保持し、次回以降の
  //     ドラッグ位置 clamp / drop 着地点 clamp で「初回だけの近似定数」でなく実寸を使える
  //     ようにする (バグ修正 2026-08-01)。
  //   - clamp 補正 (はみ出していれば onReposition) はドラッグ中はスキップする (= 親が
  //     `position` を直接動かしているので二重に動かさない)。
  // onMeasure / onReposition は親がインライン arrow で渡すため毎レンダー参照が変わる。deps に
  // 入れると親の再レンダーのたびに layout effect (= 同期 setState) が再実行されるので ref 経由にする。
  const onMeasureRef = useRef(onMeasure);
  onMeasureRef.current = onMeasure;
  const onRepositionRef = useRef(onReposition);
  onRepositionRef.current = onReposition;
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (el === null || typeof window === 'undefined') return;

    const measure = (): void => {
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      onMeasureRef.current({ width: rect.width, height: rect.height });
      if (isDragging) return;
      const clamped = clampFloatPosition(
        position,
        { width: rect.width, height: rect.height },
        { width: window.innerWidth, height: window.innerHeight },
        FLOAT_EDGE_INSET_PX,
      );
      // 厳密比較だと fractional DPR で実測高さが top 位置次第で揺れ (device-pixel snapping)、
      // clamp → reposition → 再測定 → clamp … と収束しない layout-effect ループになる。
      // sizeEquals と同じ 0.5px の許容を入れて振動を止める。
      if (
        Math.abs(clamped.x - position.x) > CLAMP_EPSILON_PX ||
        Math.abs(clamped.y - position.y) > CLAMP_EPSILON_PX
      ) {
        onRepositionRef.current(clamped);
      }
    };

    measure();
    window.addEventListener('resize', measure);
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => measure());
      ro.observe(el);
    }
    return () => {
      window.removeEventListener('resize', measure);
      ro?.disconnect();
    };
  }, [isDragging, position]);

  return (
    // biome-ignore lint/a11y/useSemanticElements: フローティングパネル本体 (modal ではないので dialog にしない、role="region" が適切な非モーダル領域).
    <div
      ref={rootRef}
      role="region"
      aria-label={label}
      data-testid={`modern-float-shell-${label}`}
      data-float-panel="true"
      // クリック/ドラッグ開始のどちらでも最前面へ (§9 z-order 要件)。capture phase なので
      // 子要素 (スライダー等) が stopPropagation してもここは必ず先に発火する。
      onPointerDownCapture={onRequestFront}
      // シェル全体を掴み所にする (バグ修正 2026-08-01)。NO_SHELL_DRAG_SELECTOR に一致する
      // 要素上では handleShellPointerDown が早期 return するので、ボタン等の click は
      // 従来通り発火する (= grip.onPointerDown も setPointerCapture も一切呼ばれない)。
      onPointerDown={handleShellPointerDown}
      onPointerMove={grip.onPointerMove}
      onPointerUp={grip.onPointerUp}
      onPointerCancel={grip.onPointerCancel}
      onLostPointerCapture={grip.onLostPointerCapture}
      // ネイティブ drag-and-drop / テキスト選択の残存経路を塞ぐ (バグ修正 2026-08-01
      // 実機報告「フロートウィンドウをドラッグしても動かないことがある」)。root 自体の
      // `draggable=false` に加え、`dragstart` はバブルするので子孫の `<img>` 等が起点でも
      // ここで一括 preventDefault できる (= ネイティブ drag image がポインタに追従する
      // 「薄いゴースト」を根絶)。`preventDefault` はマウスのテキスト選択開始も併せて
      // 抑止する (= 各パネルの見出しは既に `select-none` 済みだが、root には敢えて
      // `select-none` を掛けない: 本体は `overflow-y-auto` のスクロール領域で、チャット
      // ログ等はコピペのため意図的に選択可能にしている箇所があるため、ここで一律に潰さない)。
      //
      // touch-action: root には敢えて `none` を設定しない。touch-action はご先祖の値との
      // 交差 (intersection) で効くため、root で `none` にすると子孫 (= 本体の
      // `overflow-y-auto` スクロール領域) が `pan-y` 等で上書きしようとしても効かず、
      // タッチでのスクロールが丸ごと壊れる。タッチでの「シェル全体つかみ」は本 fix の
      // スコープ外 (= マウスのネイティブ DnD / テキスト選択漏れが今回の実害) とし、
      // グリップ帯 (= 元々スクロール対象ではない) にだけ `touch-action: none` を残す。
      draggable={false}
      onDragStart={(e) => e.preventDefault()}
      className={`fixed flex flex-col rounded-lg border bg-[var(--color-panel)] shadow-xl transition-[opacity,box-shadow] ${
        nearDock
          ? 'border-[var(--color-accent)] opacity-60 ring-2 ring-[var(--color-accent)]'
          : 'border-[var(--color-border)] opacity-95'
      } ${isDragging ? 'shadow-2xl' : ''}`}
      style={{ left: position.x, top: position.y, width: widthPx, zIndex }}
    >
      {/* グリップ帯 (= 視覚的アフォーダンス) + 「ドックへ戻す」。実際の掴み所はシェル全体
          (root の pointerdown、上記) なので、この帯自体はもう専用ハンドラを持たない
          (= 掴めることを見せるだけの装飾)。ドック内パネルと同じ `usePanelDragGrip` を
          root で共有する (§2「同じグリップ・同じ drag 経路」、2 系統にしない)。
          touch-action:none はこの帯 (スクロール対象ではない) にだけ残す。 */}
      <div className="flex items-center justify-between gap-1 rounded-t-lg bg-[var(--color-bg-soft)] px-1 py-[1px]">
        <div
          aria-hidden="true"
          data-testid="modern-float-shell-grip"
          title={`${label} を掴んで移動 / ドックへ運んで戻す (シェル全体どこでも掴めます)`}
          className="flex min-w-0 flex-1 cursor-grab select-none items-center gap-1 py-[2px] text-[10px] font-bold text-[var(--color-text-muted)]"
          style={{ touchAction: 'none' }}
        >
          <span className="opacity-60">⠿</span>
          <span className="truncate">{label}</span>
        </div>
        <button
          type="button"
          data-testid="modern-float-shell-redock"
          title={returnToDockLabel}
          aria-label={returnToDockAriaLabel}
          // NO_SHELL_DRAG_SELECTOR (button) により root 側は既に委譲しないが、念のため
          // 二重に防御する (= ColorWheelPalette の × ボタンと同じ既知パターン、
          // pointerdown が bubble して setPointerCapture が走ると click が dispatch
          // されなくなる事故を確実に避ける)。
          onPointerDown={(e) => e.stopPropagation()}
          onClick={onReturnToDock}
          className="shrink-0 rounded px-1 text-[10px] font-bold text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-white"
        >
          {returnToDockLabel}
        </button>
      </div>
      {/* 本体 (= panelRegistry の render 結果、中身は無改変)。高さは内容依存だが、ドックに
          比べ無制限に伸びうるパネル (社交ドック等) は viewport を超えないよう上限を敷き
          スクロールさせる。パネル自身の見出し (例: ModernLayerPanel の <header>) を含め、
          ボタン/入力等の対話要素以外はここを掴んでもシェルごとドラッグできる。 */}
      <div className="min-h-0 max-h-[70vh] overflow-y-auto p-1 [&>div]:h-full">{children}</div>
    </div>
  );
};
